import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { fetchWalletBalance } from "../../../Store/Slices/WalletSlice";

/* ─── Status config ─── */
const STATUS_CFG = {
  ACTIVE:   { label: "Active",   bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500",  border: "border-green-200" },
  BLOCKED:  { label: "Blocked",  bg: "bg-red-100",   text: "text-red-700",   dot: "bg-red-500",    border: "border-red-200"   },
  DISABLED: { label: "Disabled", bg: "bg-gray-100",  text: "text-gray-600",  dot: "bg-gray-400",   border: "border-gray-200"  },
};

/* ─── Txn type config ─── */
const TXN_CFG = {
  DRAW:    { label: "Draw",    bg: "bg-blue-50",  text: "text-blue-700"  },
  SETTLE:  { label: "Settle",  bg: "bg-green-50", text: "text-green-700" },
  PENALTY: { label: "Penalty", bg: "bg-red-50",   text: "text-red-700"   },
};

/* ─── Helpers ─── */
const fmt = (n) =>
  (n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

/* ════════════════════════════════════════════════════════════ */

const OdDashboard = () => {
  const dispatch = useDispatch();
  const username = useSelector((s) => s.auth.employeeDetails?.username);

  /* ─── API hooks (useApi pattern) ─── */
  // GET  /od/account/{username}  — path param baked in from Redux at hook init
  const { callApi: fetchAccountApi, loading: accountLoading } = useApi(`/od/account`, "GET");

  // GET  /od/history/{username}
  const { callApi: fetchHistoryApi, loading: historyLoading } = useApi(`/od/history`, "GET");

  // POST /od/draw
  const { callApi: drawApi } = useApi("/od/draw", "POST");

  // POST /od/settle
  const { callApi: settleApi } = useApi("/od/settle", "POST");

  /* ─── Local state ─── */
  const [account,      setAccount]      = useState(null);
  const [accountError, setAccountError] = useState(null);

  const [history,      setHistory]      = useState([]);

  const [drawAmount,    setDrawAmount]    = useState("");
  const [settleAmount,  setSettleAmount]  = useState("");
  const [drawBusy,      setDrawBusy]      = useState(false);
  const [settleBusy,    setSettleBusy]    = useState(false);

  const [popup, setPopup] = useState(null); // { success, message }

  /* ─── Load on mount ─── */
  useEffect(() => {
    loadAccount();
    loadHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Data fetchers ─── */
  const loadAccount = async () => {
    setAccountError(null);
    try {
      const res = await fetchAccountApi(null , `username=${username}`); // GET — no body, no queryParams
      setAccount(res);
    } catch (err) {
      setAccountError(err?.response?.data?.message || "Failed to load OD account.");
    }
  };

  const loadHistory = async () => {
    try {
      const res = await fetchHistoryApi(null , `username=${username}`); // GET — no body, no queryParams
      setHistory(res?.history || []);
    } catch {
      setHistory([]);
    }
  };

  /* ─── Draw handler ─── */
  const handleDraw = async () => {
    if (!drawAmount || Number(drawAmount) <= 0) {
      setPopup({ success: false, message: "Please enter a valid draw amount." });
      return;
    }
    setDrawBusy(true);
    try {
      const res = await drawApi({ username, amount: Number(drawAmount) });
      setPopup({ success: res?.success !== false, message: res?.message || "Draw successful." });
      setDrawAmount("");
      loadAccount();
      loadHistory();
      dispatch(fetchWalletBalance());
    } catch (err) {
      setPopup({ success: false, message: err?.response?.data?.message || "Draw failed. Try again." });
    } finally {
      setDrawBusy(false);
    }
  };

  /* ─── Settle handler ─── */
  const handleSettle = async () => {
    if (!settleAmount || Number(settleAmount) <= 0) {
      setPopup({ success: false, message: "Please enter a valid settlement amount." });
      return;
    }
    setSettleBusy(true);
    try {
      const res = await settleApi({ username, amount: Number(settleAmount) });
      setPopup({ success: res?.success !== false, message: res?.message || "Settlement successful." });
      setSettleAmount("");
      loadAccount();
      loadHistory();
      dispatch(fetchWalletBalance());
    } catch (err) {
      setPopup({ success: false, message: err?.response?.data?.message || "Settlement failed. Try again." });
    } finally {
      setSettleBusy(false);
    }
  };

  /* ─── Derived ─── */
  const statusCfg = account ? (STATUS_CFG[account.status] || STATUS_CFG.DISABLED) : null;
  const canDraw   = account?.status === "ACTIVE";
  const canSettle = (account?.status === "ACTIVE" || account?.status === "BLOCKED") && (account?.utilizedAmount ?? 0) > 0;

  /* ══════════════════════ RENDER ══════════════════════ */
  return (
    <div className="min-h-screen bg-white px-4 md:px-8 py-8">

      {/* ── Header ── */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-[#12345A]">OD Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">
          Overdraft account overview · draw · settle · transaction history
        </p>
      </div>

      {/* ── OD Settlement Notice ── */}
      <div className="mb-8 rounded-2xl border border-amber-300 bg-amber-50 overflow-hidden">
        {/* Header strip */}
        <div className="flex items-center gap-3 bg-amber-100 border-b border-amber-300 px-5 py-3">
          <span className="text-amber-600 text-xl">⚠️</span>
          <p className="text-amber-800 font-bold text-base tracking-wide">OD Settlement Notice</p>
        </div>

        <div className="px-5 py-5">
          <p className="text-amber-900 text-sm leading-relaxed">
            The system will automatically settle the outstanding OD amount <span className="font-semibold">every night</span>.
            If sufficient balance is available in the main wallet, the OD amount will be adjusted automatically.
          </p>

          <p className="mt-3 text-amber-900 text-sm">
            A penalty / charge of{" "}
            <span className="inline-block bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-md text-xs">
              0.50% per day
            </span>{" "}
            will be applied on the <span className="font-semibold">outstanding amount</span> until it is fully settled.
          </p>

          {/* Example box */}
          <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 px-5 py-4">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">Example</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-gray-400 uppercase tracking-wide">OD Limit</span>
                <span className="text-base font-bold text-[#12345A]">₹2,00,000</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-gray-400 uppercase tracking-wide">Wallet Balance at Settlement</span>
                <span className="text-base font-bold text-green-700">₹1,50,000</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-gray-400 uppercase tracking-wide">Outstanding (Penalty Applies)</span>
                <span className="text-base font-bold text-red-600">₹50,000</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      {accountLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : accountError ? (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-600 font-medium mb-3">{accountError}</p>
          <button
            onClick={loadAccount}
            className="text-sm text-[#12345A] underline font-semibold"
          >
            Retry
          </button>
        </div>
      ) : account && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">

          {/* Credit Limit */}
          <div className="bg-[#0A2A4F] rounded-2xl p-5 text-white">
            <p className="text-xs text-white/60 uppercase tracking-wide mb-2">Credit Limit</p>
            <p className="text-2xl font-bold">₹{fmt(account.creditLimit)}</p>
            <p className="text-xs text-white/40 mt-2">Total OD line</p>
          </div>

          {/* Utilized */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Utilized</p>
            <p className="text-2xl font-bold text-orange-500">₹{fmt(account.utilizedAmount)}</p>
            <p className="text-xs text-gray-400 mt-2">Amount drawn</p>
          </div>

          {/* Available */}
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Available</p>
            <p className="text-2xl font-bold text-green-600">₹{fmt(account.availableLimit)}</p>
            <p className="text-xs text-gray-400 mt-2">Ready to draw</p>
          </div>

          {/* Status */}
          <div className={`border rounded-2xl p-5 ${statusCfg.border}`}>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Status</p>
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${statusCfg.bg} ${statusCfg.text}`}>
              <span className={`w-2 h-2 rounded-full ${statusCfg.dot}`} />
              {statusCfg.label}
            </span>
            {account.lastDrawDate && (
              <p className="text-xs text-gray-400 mt-2">Last draw: {account.lastDrawDate}</p>
            )}
          </div>

        </div>
      )}

      {/* ── Blocked Warning ── */}
      {account?.status === "BLOCKED" && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4 flex gap-3 items-start">
          <span className="text-red-500 text-xl mt-0.5">⚠️</span>
          <div>
            <p className="text-red-700 font-bold text-sm">OD Account Blocked — Missed EOD Settlement</p>
            <p className="text-red-600 text-xs mt-1">
              A penalty fee will be deducted automatically on your next settlement.
              Drawing is disabled until the outstanding balance is fully cleared.
            </p>
          </div>
        </div>
      )}

      {/* ── Disabled Notice ── */}
      {account?.status === "DISABLED" && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-2xl p-4 text-center text-gray-500 text-sm">
          Your OD account has been disabled by admin. Please contact support.
        </div>
      )}

      {/* ── Draw & Settle ── */}
      {account && account.status !== "DISABLED" && (
        <div className="grid md:grid-cols-2 gap-6 mb-10">

          {/* ─ Draw ─ */}
          <div className="border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-[#0A2A4F] flex items-center justify-center text-white font-bold text-base">↓</div>
              <h2 className="text-lg font-bold text-[#12345A]">Draw from OD</h2>
            </div>
            <p className="text-xs text-gray-400 mb-6 ml-12">Transfer OD credit into your main wallet</p>

            <label className="text-gray-500 font-semibold text-xs uppercase tracking-wide block mb-2">
              Amount (₹)
            </label>
            <input
              type="number"
              value={drawAmount}
              onChange={(e) => setDrawAmount(e.target.value)}
              disabled={!canDraw}
              placeholder="0.00"
              className="w-full rounded-none bg-transparent border-b-2 border-gray-200 text-gray-900 px-2 py-3 text-xl focus:outline-none focus:border-[#12345A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            />
            {!canDraw && account.status === "BLOCKED" && (
              <p className="text-xs text-red-400 mt-2">
                Draw blocked — settle your outstanding balance first.
              </p>
            )}

            <button
              onClick={handleDraw}
              disabled={!canDraw || drawBusy}
              className="mt-5 w-full bg-[#12345A] text-white font-bold py-3.5 rounded-xl hover:bg-[#0d2644] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {drawBusy ? <><Spinner /> Processing...</> : "Draw Funds"}
            </button>
          </div>

          {/* ─ Settle ─ */}
          <div className="border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center text-white font-bold text-base">↑</div>
              <h2 className="text-lg font-bold text-[#12345A]">Settle OD</h2>
            </div>
            <p className="text-xs text-gray-400 mb-6 ml-12">
              Repay from main wallet
              {account.status === "BLOCKED" ? " · Penalty fee applies" : ""}
            </p>

            <label className="text-gray-500 font-semibold text-xs uppercase tracking-wide block mb-2">
              Amount (₹)
            </label>
            <input
              type="number"
              value={settleAmount}
              onChange={(e) => setSettleAmount(e.target.value)}
              disabled={(account.utilizedAmount ?? 0) === 0}
              placeholder="0.00"
              className="w-full rounded-none bg-transparent border-b-2 border-gray-200 text-gray-900 px-2 py-3 text-xl focus:outline-none focus:border-[#12345A] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            />
            {(account.utilizedAmount ?? 0) === 0 ? (
              <p className="text-xs text-green-600 mt-2 font-medium">
                Balance is fully clear — nothing to settle.
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-2">
                Outstanding: ₹{fmt(account.utilizedAmount)} · Partial settlement allowed
              </p>
            )}

            <button
              onClick={handleSettle}
              disabled={!canSettle || settleBusy}
              className="mt-5 w-full bg-green-600 text-white font-bold py-3.5 rounded-xl hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {settleBusy ? <><Spinner /> Processing...</> : "Settle Now"}
            </button>
          </div>

        </div>
      )}

      {/* ── Transaction History ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#12345A]">Transaction History</h2>
          <button
            onClick={loadHistory}
            disabled={historyLoading}
            className="text-xs text-[#12345A] border border-[#12345A]/20 px-3 py-1.5 rounded-lg hover:bg-[#12345A]/5 transition disabled:opacity-40"
          >
            {historyLoading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {historyLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-16 text-gray-300">
            <p className="text-5xl mb-3">📋</p>
            <p className="font-semibold text-gray-400">No transactions yet</p>
            <p className="text-xs text-gray-300 mt-1">Draw or settle to see history here</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#0A2A4F] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Txn ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide">Balance After</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Remarks</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {history.map((txn, idx) => {
                  const cfg = TXN_CFG[txn.txnType] || { label: txn.txnType, bg: "bg-gray-50", text: "text-gray-700" };
                  return (
                    <tr key={txn.txnId} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{txn.txnId}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">₹{fmt(txn.amount)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">₹{fmt(txn.utilizedAfter)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{txn.remarks}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {txn.createdAt?.replace("T", " ") ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Result Popup ── */}
      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-[0_10px_40px_rgb(0,0,0,0.1)] border border-gray-100 w-full max-w-sm p-8 text-center">
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${popup.success ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
              {popup.success ? (
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-[#12345A] text-lg font-bold mb-8">{popup.message}</p>
            <button
              onClick={() => setPopup(null)}
              className="w-full bg-[#12345A] text-white font-bold py-3.5 rounded-xl hover:bg-[#0d2644] transition"
            >
              Done
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default OdDashboard;
